import { fmt, type SaleJournal } from '@k2pos/core';
import { Link, useParams } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { InvoiceCard } from '@/components/InvoiceCard';
import { Mono, Text } from '@/components/ui/Text';
import { IconChevronRight } from '@/components/icons';
import { can, post } from '@/bridge';
import { useGoBack } from '@/hooks/useNav';
import { useSaveBill } from '@/hooks/useSaveBill';
import { useShopQr } from '@/hooks/useShopQr';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './OrderDetail.module.css';

/** One past bill, re-openable and re-printable. */
export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const goBack = useGoBack('/orders');
  const shop = usePos((s) => s.shop);
  const order = usePos((s) =>
    s.journal.find((e): e is SaleJournal => e.kind === 'sale' && e.id === id),
  );
  // Called before the early return below, so the hook order stays stable.
  const qr = useShopQr();
  const { save: saveBill, busy: saving, error: saveError, dismiss } = useSaveBill();

  if (!order) {
    return (
      <>
        <AppBar title="ບໍ່ພົບບິນ" onBack={goBack} />
        <div className={styles.empty}>
          <Text size={12.5} color={colors.textMuted} align="center">
            ບໍ່ພົບບິນນີ້ໃນປະຫວັດ
          </Text>
        </div>
      </>
    );
  }

  const credit = order.payMode === 'credit';

  function print() {
    if (can('print')) post('print.receipt', { order });
    else window.print();
  }

  /** The bill as a picture — see `Done.saveImage` for why it is not text. */
  function saveImage() {
    if (!order) return;
    saveBill({
      shopName: shop.name,
      phone: shop.phone,
      address: shop.address,
      invoiceNo: order.invoiceNo,
      at: order.at,
      customerName: order.customerName,
      credit,
      lines: order.lines.map((l) => ({
        name: l.name,
        unit: l.unit,
        qty: l.qty,
        amount: l.amount,
      })),
      total: order.total,
      bank: shop.bankNote,
      qr,
      footer: shop.invoiceFooter,
    });
  }

  return (
    <>
      <AppBar
        title={`#${order.invoiceNo}`}
        onBack={goBack}
        subtitle={
          <Text size={11} color={colors.onDarkSoft}>
            {credit ? 'ຕິດໜີ້' : 'ຈ່າຍເລີຍ'} · {order.customerName ?? 'ລູກຄ້າທົ່ວໄປ'}
          </Text>
        }
      />

      <div className={`${styles.body} vscroll`}>
        <InvoiceCard
          shopName={shop.name}
          shopPhone={shop.phone}
          shopAddress={shop.address}
          invoiceNo={order.invoiceNo}
          at={order.at}
          customerName={order.customerName}
          credit={credit}
          total={order.total}
          bank={shop.bankNote}
          qr={qr}
          footer={shop.invoiceFooter}
          lines={order.lines.map((l) => ({
            productId: l.productId,
            name: l.name,
            unit: l.unit,
            qty: l.qty,
            amount: l.amount,
          }))}
        />

        {order.customerId ? (
          <Link to={`/customer/${order.customerId}`} className={styles.customerLink}>
            <Card className={styles.customerRow}>
              <div className="grow col">
                <Text size={11} color={colors.textMuted}>
                  ລູກຄ້າ
                </Text>
                <Text weight={700} size={13.5} lines={1} align="left">
                  {order.customerName}
                </Text>
              </div>
              <IconChevronRight size={17} className={styles.chevron} />
            </Card>
          </Link>
        ) : null}

        {/* Cost is on the journal but never on the printed bill, so it is shown
            here as the shop's own figure rather than inside the invoice. */}
        {order.cogs > 0 ? (
          <Card className={styles.marginRow}>
            <div className="grow col">
              <Text size={11} color={colors.textMuted}>
                ກຳໄລບິນນີ້
              </Text>
              <Mono size={10.5} color={colors.textFaint}>
                ຕົ້ນທຶນ {fmt(order.cogs)} ₭
                {order.costedRevenue < order.total ? ' · ນັບບໍ່ຄົບທຸກລາຍການ' : ''}
              </Mono>
            </div>
            <Mono weight={700} size={15} color={colors.skyDeep}>
              {fmt(order.total - order.cogs)} ₭
            </Mono>
          </Card>
        ) : null}

        <ErrorBanner message={saveError} onDismiss={dismiss} />

        <div className={styles.actions}>
          <Button label="ພິມ" className="grow" onPress={print} />
          <Button
            label={saving ? 'ກຳລັງສ້າງຮູບ…' : 'ບັນທຶກຮູບ'}
            variant="outline"
            className="grow"
            disabled={saving}
            onPress={saveImage}
          />
        </div>
      </div>
    </>
  );
}
